import { useState } from "react";
import { api } from "../lib/api.js";
import { Mostarda } from "../components/Mostarda.js";

/**
 * Pareamento do app de sincronia do Android.
 *
 * Esta tela existe porque o app Android NÃO PODE ter login. A autenticação do
 * Mostarda é passkey, e dentro de uma WebView do Android
 * `navigator.credentials.get()` existe e não faz nada — um bridge nativo para o
 * Credential Manager resolveria, mas exigiria registrar uma SEGUNDA passkey,
 * já que a WebView não compartilha credencial com o Chrome.
 *
 * Então o pareamento acontece aqui, no Chrome, onde a passkey funciona: esta
 * tela emite um token de ingestão e abre `mostarda://parear`. O Android entrega
 * ao app, que guarda e nunca mais precisa de sessão.
 */

interface TokenEmitido {
  id: string;
  secret: string;
  authMode: "hmac" | "bearer";
  endpoint: string;
}

export function PareaAndroid({ aoFechar }: { aoFechar: () => void }) {
  const [token, setToken] = useState<TokenEmitido | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function emitir() {
    setOcupado(true);
    setErro(null);
    try {
      // `health_connect` sai em modo `hmac`: o segredo fica no aparelho e nunca
      // trafega, ao contrário do token do Atalho do iPhone, que é `bearer`
      // porque o app Atalhos não tem ação de hash.
      const t = await api.post<TokenEmitido>("/settings/ingest-tokens", {
        source: "health_connect",
        label: "App Android",
      });
      setToken(t);
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não consegui criar o token.");
    } finally {
      setOcupado(false);
    }
  }

  // O segredo vai no FRAGMENTO, não na query: fragmento não é enviado a
  // servidor nenhum e tende a ficar fora de log. Ver apps/android/src/pareamento.ts,
  // que é o dono deste formato e tem teste da volta completa.
  const link =
    token &&
    `mostarda://parear#${new URLSearchParams({
      e: token.endpoint,
      t: token.id,
      s: token.secret,
    }).toString()}`;

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col gap-6 px-5 py-8">
      <header className="flex items-center gap-3">
        <Mostarda estado={token ? "cutucando" : "espreitando"} className="h-16 w-16" />
        <div>
          <h1 className="text-lg font-semibold">Parear o app Android</h1>
          <p className="text-sm text-suave">Para o Health Connect chegar sozinho.</p>
        </div>
      </header>

      {!token && (
        <>
          <div className="card">
            <p className="text-sm text-suave">
              O app de sincronia não tem tela de login — sua senha é uma passkey, e passkey não
              funciona dentro da janela de um app Android. Este botão cria a credencial no lugar
              dela.
            </p>
          </div>

          <ol className="space-y-3 text-sm text-suave">
            <li>
              <span className="font-medium text-texto">1.</span> Instale o APK (baixe em{" "}
              <b>Actions → APK</b> no GitHub).
            </li>
            <li>
              <span className="font-medium text-texto">2.</span> Abra <b>esta página no Chrome do
              mesmo celular</b> — de outro aparelho o link não tem para onde ir.
            </li>
            <li>
              <span className="font-medium text-texto">3.</span> Toque no botão abaixo.
            </li>
          </ol>

          {erro && <p className="text-sm text-alerta">{erro}</p>}

          <button className="botao-primario" onClick={() => void emitir()} disabled={ocupado}>
            {ocupado ? "Criando…" : "Criar credencial"}
          </button>
          <button className="text-sm text-suave underline" onClick={aoFechar}>
            agora não
          </button>
        </>
      )}

      {token && link && (
        <>
          <div className="card">
            <p className="text-sm">
              Credencial criada. <b>Abra o app agora</b> — o segredo aparece uma vez só e não é
              guardado aqui.
            </p>
          </div>

          <a className="botao-primario block text-center" href={link}>
            Abrir o Mostarda Sync
          </a>

          <p className="text-xs text-suave">
            Se nada acontecer, o app não está instalado. Instale o APK e volte a esta tela — mas
            crie uma credencial nova, porque esta já não pode mais ser lida.
          </p>
          <p className="text-xs text-suave">
            Deu errado no meio? Revogue em <b>Ajustes → Tokens de sincronização</b> e comece de
            novo. Um token solto por aí escreve no seu histórico, mesmo sem conseguir ler nada.
          </p>

          <button className="text-sm text-suave underline" onClick={aoFechar}>
            terminei
          </button>
        </>
      )}
    </main>
  );
}
